export const PROMO_SURFACES = [
  {
    key: 'corner_ribbon',
    title: 'Corner ribbon',
    summary: 'Top-right text ribbon.',
    creative: 'Text only.',
  },
  {
    key: 'corner_video',
    title: 'Corner video',
    summary: 'Floating portrait video.',
    creative: 'Video only.',
  },
  {
    key: 'tab_bar',
    title: 'Tab bar',
    summary: 'Idle replacement of the bottom tab strip.',
    creative: 'Video or a remote Lottie file.',
  },
];

export const TEMPLATE_STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
];

export const BLOCKER_LABELS = {
  INGEST_UPLOADING: 'The video is still uploading.',
  INGEST_PROCESSING: 'The video is still converting.',
  INGEST_FAILED: 'Video conversion failed.',
  MISSING_M3U8: 'This video has no HLS playlist yet.',
  MISSING_LOTTIE_URI: 'Add a Lottie URL.',
  MISSING_LABEL: 'The label is only spaces.',
  MISSING_LINK: 'Add a link.',
  MISSING_DISPLAY: 'Add the display rules.',
};

const DISPLAY_FIELDS = [
  ['max_shows_per_session', 'Max shows per session', 0],
  ['max_shows_per_day', 'Max shows per day', 0],
  ['delay_before_show_sec', 'Delay before show', 0],
  ['visible_duration_sec', 'Visible duration', 1],
  ['cooldown_sec', 'Cooldown', 0],
];

export function getPromoSurface(surfaceKey) {
  return PROMO_SURFACES.find((surface) => surface.key === surfaceKey) || null;
}

export function emptyTemplateForm(surfaceKey) {
  const display =
    surfaceKey === 'corner_ribbon'
      ? {
          max_shows_per_session: '2',
          max_shows_per_day: '5',
          delay_before_show_sec: '0',
          visible_duration_sec: '12',
          cooldown_sec: '300',
          suppress_after_tap_for_session: true,
        }
      : surfaceKey === 'tab_bar'
        ? {
            max_shows_per_session: '3',
            max_shows_per_day: '8',
            delay_before_show_sec: '1',
            visible_duration_sec: '15',
            cooldown_sec: '120',
            suppress_after_tap_for_session: true,
          }
        : {
            max_shows_per_session: '1',
            max_shows_per_day: '3',
            delay_before_show_sec: '2',
            visible_duration_sec: '20',
            cooldown_sec: '600',
            suppress_after_tap_for_session: true,
          };

  return {
    name: '',
    make_active: true,
    label: '',
    link: '',
    display,
    creative: surfaceKey === 'tab_bar' ? 'lottie_remote' : surfaceKey === 'corner_ribbon' ? 'text' : 'video',
    video_source: 'transcode',
    m3u8_url: '',
    mp4_url: '',
    thumbnail_url: '',
    lottie_uri: '',
    min_app_version: '',
    max_app_version: '',
  };
}

/** Empty input means no bound. `2.3` and `v2.3.0` become `2.3.0`. Prerelease and build metadata are rejected. */
export function parseAppVersion(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return { empty: true, version: null };
  const withoutPrefix = raw.replace(/^v/i, '');
  if (!/^\d+\.\d+(?:\.\d+)?$/.test(withoutPrefix)) return { empty: false, version: null };
  const [major, minor, patch = '0'] = withoutPrefix.split('.');
  return { empty: false, version: `${Number(major)}.${Number(minor)}.${Number(patch)}` };
}

export function compareAppVersions(left, right) {
  const a = left.split('.').map(Number);
  const b = right.split('.').map(Number);
  for (let index = 0; index < 3; index += 1) {
    if (a[index] !== b[index]) return a[index] - b[index];
  }
  return 0;
}

export function formatVersionWindow(min, max) {
  if (!min && !max) return 'Every version';
  if (min && max && min === max) return min;
  if (min && max) return `${min}–${max}`;
  if (min) return `${min} and newer`;
  return `${max} and older`;
}

function versionBoundsError(form) {
  const min = parseAppVersion(form?.min_app_version);
  const max = parseAppVersion(form?.max_app_version);
  if (!min.empty && !min.version) return 'min_app_version must be a version like 2.3.0';
  if (!max.empty && !max.version) return 'max_app_version must be a version like 2.3.0';
  if (min.version && max.version && compareAppVersions(min.version, max.version) > 0) {
    return 'min_app_version must be less than or equal to max_app_version';
  }
  return '';
}

function displayFromTemplate(display, fallback) {
  const source = display || {};
  return {
    max_shows_per_session: String(source.max_shows_per_session ?? fallback.max_shows_per_session),
    max_shows_per_day: String(source.max_shows_per_day ?? fallback.max_shows_per_day),
    delay_before_show_sec: String(source.delay_before_show_sec ?? fallback.delay_before_show_sec),
    visible_duration_sec: String(source.visible_duration_sec ?? fallback.visible_duration_sec),
    cooldown_sec: String(source.cooldown_sec ?? fallback.cooldown_sec),
    suppress_after_tap_for_session:
      source.suppress_after_tap_for_session ?? fallback.suppress_after_tap_for_session,
  };
}

export function templateToForm(template, surfaceKey) {
  const base = emptyTemplateForm(surfaceKey);
  if (!template) return base;
  const media = template.media || null;
  const ingest = template.ingest || null;
  let creative = base.creative;
  if (surfaceKey === 'corner_ribbon') creative = 'text';
  else if (media?.kind === 'lottie_remote') creative = 'lottie_remote';
  else creative = 'video';

  let videoSource = 'transcode';
  if (ingest?.source === 'direct_m3u8') videoSource = 'direct_m3u8';
  else if (ingest?.source === 'transcode') videoSource = 'transcode';
  else if (media?.kind === 'video' && media.m3u8_url) videoSource = 'direct_m3u8';

  const next = {
    ...base,
    name: template.name || '',
    label: template.label || '',
    link: template.link || '',
    display: displayFromTemplate(template.display, base.display),
    creative,
    video_source: videoSource,
    m3u8_url: media?.m3u8_url || '',
    mp4_url: media?.mp4_url || '',
    thumbnail_url: media?.thumbnail_url || '',
    lottie_uri: media?.uri || '',
    min_app_version: template.min_app_version || '',
    max_app_version: template.max_app_version || '',
  };
  delete next.make_active;
  return next;
}

export function isHttpsUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && Boolean(url.hostname);
  } catch {
    return false;
  }
}

export function isValidPromoLink(value) {
  if (typeof value !== 'string' || !value) return false;
  if (value.startsWith('/') && !value.startsWith('//')) return true;
  if (value.startsWith('dugout://') && value.length > 'dugout://'.length) return true;
  return isHttpsUrl(value);
}

function validateDisplay(display) {
  for (const [key, label, min] of DISPLAY_FIELDS) {
    const raw = String(display?.[key] ?? '').trim();
    if (!/^\d+$/.test(raw) || Number(raw) < min) {
      return min === 0
        ? `${label} must be a whole number, 0 or more.`
        : `${label} must be a whole number of at least ${min}.`;
    }
  }
  if (typeof display?.suppress_after_tap_for_session !== 'boolean') {
    return 'Choose whether a tap hides the slot for the rest of the session.';
  }
  return '';
}

export function validateTemplateForm(form, surfaceKey) {
  const name = String(form?.name || '').trim();
  if (!name || name.length > 120) return 'Name is required and must be 1–120 characters.';

  const label = String(form?.label || '').trim();
  const link = String(form?.link || '').trim();
  if (label.length > 80) return 'Label must be 80 characters or fewer.';
  if (link && !isValidPromoLink(link)) {
    return 'Link must be a /path, a dugout:// link, or an https:// URL.';
  }

  const displayError = validateDisplay(form?.display);
  if (displayError) return displayError;
  const boundsError = versionBoundsError(form);
  if (boundsError) return boundsError;
  if (!getPromoSurface(surfaceKey)) return 'Unknown slot.';
  if (surfaceKey === 'corner_ribbon') return '';

  if (surfaceKey === 'corner_video' && form.creative === 'lottie_remote') {
    return 'Corner video only accepts a video.';
  }

  if (form.creative === 'lottie_remote') {
    const uri = String(form.lottie_uri || '').trim();
    if (uri && !isHttpsUrl(uri)) return 'Lottie URL must be an https:// address.';
    return '';
  }

  if (form.video_source === 'direct_m3u8') {
    if (!isHttpsUrl(String(form.m3u8_url || '').trim())) {
      return 'Direct HLS needs an https playlist URL.';
    }
    if (String(form.mp4_url || '').trim() && !isHttpsUrl(String(form.mp4_url).trim())) {
      return 'MP4 URL must be an https:// address, or left empty.';
    }
    if (String(form.thumbnail_url || '').trim() && !isHttpsUrl(String(form.thumbnail_url).trim())) {
      return 'Poster URL must be an https:// address, or left empty.';
    }
    return '';
  }

  if (form.video_source !== 'transcode') return 'Choose an MP4 upload or a direct HLS playlist.';
  return '';
}

function displayToPayload(display) {
  return {
    max_shows_per_session: Number(display.max_shows_per_session),
    max_shows_per_day: Number(display.max_shows_per_day),
    delay_before_show_sec: Number(display.delay_before_show_sec),
    visible_duration_sec: Number(display.visible_duration_sec),
    cooldown_sec: Number(display.cooldown_sec),
    suppress_after_tap_for_session: Boolean(display.suppress_after_tap_for_session),
  };
}

export function buildTemplateBody(form, surfaceKey, { includeStatus = false } = {}) {
  const min = parseAppVersion(form.min_app_version);
  const max = parseAppVersion(form.max_app_version);
  const body = {
    name: String(form.name || '').trim(),
    display: displayToPayload(form.display),
    min_app_version: min.version,
    max_app_version: max.version,
  };
  const label = String(form.label || '').trim();
  const link = String(form.link || '').trim();
  if (label) body.label = label;
  if (link) body.link = link;
  if (includeStatus) body.status = form.make_active === false ? 'draft' : 'active';
  if (surfaceKey === 'corner_ribbon') return body;

  if (form.creative === 'lottie_remote') {
    const uri = String(form.lottie_uri || '').trim();
    if (uri) body.media = { kind: 'lottie_remote', uri };
    return body;
  }

  if (form.video_source === 'direct_m3u8') {
    body.video_source = 'direct_m3u8';
    body.media = {
      kind: 'video',
      m3u8_url: String(form.m3u8_url || '').trim(),
      mp4_url: String(form.mp4_url || '').trim() || null,
      thumbnail_url: String(form.thumbnail_url || '').trim() || null,
    };
    return body;
  }

  body.video_source = 'transcode';
  return body;
}

export function parseTemplate(data) {
  if (!data || typeof data !== 'object') return null;
  if (data.id) return data;
  if (data.template?.id) return data.template;
  return null;
}

export function parseTemplateList(data) {
  const templates = Array.isArray(data?.templates) ? data.templates : [];
  return {
    templates,
    limit: Number(data?.limit) || 20,
    offset: Number(data?.offset) || 0,
    total: Number(data?.total) || 0,
  };
}

export function parseSurfaceList(data) {
  return {
    surfaces: Array.isArray(data?.surfaces) ? data.surfaces : [],
  };
}

export function parseNestedTemplates(surface) {
  const page = surface?.templates;
  return {
    templates: Array.isArray(page?.items) ? page.items : [],
    limit: Number(page?.limit) || 20,
    offset: Number(page?.offset) || 0,
    total: Number(page?.total) || 0,
    nextCursor: page?.next_cursor ?? null,
  };
}

export function parseLivePreview(data) {
  const source = data && typeof data === 'object' ? data : {};
  return {
    campaign_id: source.campaign_id ?? null,
    corner_ribbon: source.corner_ribbon ?? null,
    corner_video: source.corner_video ?? null,
    tab_bar: source.tab_bar ?? null,
  };
}

function creativeKind(template) {
  if (template?.media?.kind === 'lottie_remote' || template?.type === 'lottie') return 'lottie';
  if (
    template?.media?.kind === 'video' ||
    template?.type === 'video' ||
    template?.surface_key === 'corner_video' ||
    template?.ingest?.source === 'transcode' ||
    template?.ingest?.source === 'direct_m3u8'
  ) {
    return 'video';
  }
  return 'ribbon';
}

/** Client mirror of the server blockers. An in-flight or failed ingest does not also report MISSING_M3U8. */
export function deriveEligibilityBlockers(template) {
  if (!template) return [];
  const blockers = [];
  const kind = creativeKind(template);
  const ingestStatus = template.ingest?.status ?? template.ingest_status ?? null;
  if (kind === 'video' && (ingestStatus === 'uploading' || ingestStatus === 'processing' || ingestStatus === 'failed')) {
    blockers.push(
      ingestStatus === 'uploading' ? 'INGEST_UPLOADING' : ingestStatus === 'processing' ? 'INGEST_PROCESSING' : 'INGEST_FAILED'
    );
  } else if (kind === 'video') {
    const playlist = template.media?.m3u8_url || template.ingest?.m3u8_url;
    if (!playlist) blockers.push('MISSING_M3U8');
  } else if (kind === 'lottie' && !template.media?.uri) {
    blockers.push('MISSING_LOTTIE_URI');
  }
  if (typeof template.label === 'string' && template.label.length > 0 && !template.label.trim()) {
    blockers.push('MISSING_LABEL');
  }
  if (!String(template.link || '').trim()) blockers.push('MISSING_LINK');
  if (!template.display || typeof template.display !== 'object') blockers.push('MISSING_DISPLAY');
  return blockers;
}

export function templateBlockers(template) {
  if (!template) return [];
  if (Array.isArray(template.blockers)) return template.blockers;
  return deriveEligibilityBlockers(template);
}

export function canActivateTemplate(template) {
  if (!template) return false;
  if (typeof template.can_activate === 'boolean') return template.can_activate;
  return templateBlockers(template).length === 0;
}

export function isTemplateActive(template) {
  return template?.is_active === true || template?.status === 'active';
}

export function describeBlockers(blockers) {
  if (!Array.isArray(blockers) || blockers.length === 0) return '';
  return blockers.map((code) => BLOCKER_LABELS[code] || code).join(' ');
}

/** Read data.code. The message text is not the contract. */
export function promoFailureMessage(res) {
  const code = res?.data?.code;
  if (code === 'NO_ACTIVE_TEMPLATE') {
    return 'Turn this surface on after choosing an active template.';
  }
  if (code === 'TEMPLATE_NOT_ELIGIBLE') {
    const detail = describeBlockers(res?.data?.blockers);
    return detail ? `The active template isn't ready. ${detail}` : "The active template isn't ready.";
  }
  return '';
}

export function warningLabel(warning) {
  if (warning === 'ON_BUT_NO_ACTIVE_TEMPLATE') return 'This surface is on, but no template is active.';
  if (warning === 'ACTIVE_TEMPLATE_NOT_ELIGIBLE') {
    return 'The active template is not eligible, so the app is not showing this slot.';
  }
  return '';
}

export function creativeTypeLabel(type) {
  if (type === 'ribbon') return 'Text ribbon';
  if (type === 'video') return 'Video';
  if (type === 'lottie') return 'Lottie';
  return type || '';
}

function versionMatchesTemplate(template, appVersion) {
  if (!appVersion) return true;
  const parsed = parseAppVersion(appVersion);
  if (!parsed.version) return false;
  if (template.min_app_version && compareAppVersions(parsed.version, template.min_app_version) < 0) return false;
  if (template.max_app_version && compareAppVersions(parsed.version, template.max_app_version) > 0) return false;
  return true;
}

function campaignToken(slot, appVersion) {
  if (!slot?.enabled || !slot.template?.id) return 'none';
  const template = slot.template;
  if (template.status && template.status !== 'active' && template.is_active !== true) return 'none';
  const blockers = Array.isArray(template.blockers) ? template.blockers : deriveEligibilityBlockers(template);
  if (blockers.length) return 'none';
  if (!versionMatchesTemplate(template, appVersion)) return 'none';
  return template.id;
}

export function composeCampaignId(ribbon, video, tab, appVersion) {
  const parts = [campaignToken(ribbon, appVersion), campaignToken(video, appVersion), campaignToken(tab, appVersion)];
  if (parts.every((part) => part === 'none')) return null;
  return `promo_${parts.join('_')}`;
}

export function templateAcceptsIngest(template) {
  if (!template?.id) return false;
  if (template.surface_key === 'corner_ribbon') return false;
  if (template.media?.kind === 'lottie_remote') return false;
  if (template.ingest?.source === 'direct_m3u8') return false;
  return template.surface_key === 'corner_video' || template.surface_key === 'tab_bar';
}

export function statusLabel(status) {
  if (status === 'active') return 'Active';
  if (status === 'draft') return 'Draft';
  return status || '—';
}

export function templateIngestStatus(template) {
  return template?.ingest?.status ?? template?.ingest_status ?? null;
}

export function ingestStatusLabel(status) {
  if (status == null || status === '') return '—';
  if (status === 'none') return 'No video';
  if (status === 'uploading') return 'Uploading';
  if (status === 'processing') return 'Converting';
  if (status === 'ready') return 'Ready';
  if (status === 'failed') return 'Failed';
  return status;
}

export function formatTimestamp(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function activateResultMessage(data) {
  if (data?.replaced_template_id || data?.deactivated_template_id) {
    return 'This template is active. The previous one on this slot is a draft. The other slots are unchanged.';
  }
  return 'This template is active. The other slots are unchanged.';
}

export function createResultMessage(data) {
  const template = data?.template && typeof data.template === 'object' ? data.template : null;
  if (data?.activated) {
    return data.replaced_template_id
      ? 'This template is active. The previous one on this slot is a draft. The other slots are unchanged.'
      : 'This template is active. The other slots are unchanged.';
  }
  if (template?.activate_on_ready) {
    return 'Saved as a draft. It becomes active when the video is ready, if it is still eligible.';
  }
  const detail = describeBlockers(data?.blockers);
  if (detail) return `Saved as a draft. ${detail}`;
  return 'Saved as a draft.';
}

export function surfaceSwitchMessage(data, title) {
  if (data?.enabled) {
    if (data?.restored_template?.name) {
      return `${title} is on. Restored “${data.restored_template.name}”.`;
    }
    return `${title} is on.`;
  }
  return `${title} is off. The active template is unchanged.`;
}

export function clearActiveMessage(title) {
  return `Removed from ${title}. That template is a draft, and the On/Off switch is unchanged.`;
}
